/**
 * The guides registry. Each guide is a markdown file in this directory,
 * imported raw at build time; the reader renders it with the house
 * renderer in lib/guide-markdown.tsx. To add a guide: drop the .md file
 * here and add one entry below, in reading order.
 *
 * Content basis: Apollo Academy scrub + practitioner-forum sweeps,
 * 3 Sep 2026 (workflow wf_d4e0f214-a51), critiqued for unsourced stats
 * and house style before landing here.
 */

import howColdEmailWorks from './how-cold-email-works-in-2026.md?raw';
import buildingYourList from './building-your-list.md?raw';
import signalsAndTiming from './signals-and-timing.md?raw';
import deliverability from './deliverability.md?raw';
import writingEmails from './writing-emails-that-get-replies.md?raw';
import followUps from './follow-ups-and-sequences.md?raw';
import beyondEmail from './beyond-email.md?raw';
import measuringResults from './measuring-results.md?raw';
import economics from './the-economics-of-cold-outreach.md?raw';
import tacticsThatDied from './tactics-that-died.md?raw';

export interface GuideMeta {
  slug: string;
  title: string;
  oneLiner: string;
  markdown: string;
}

export const guides: GuideMeta[] = [
  {
    slug: 'how-cold-email-works-in-2026',
    title: 'How Cold Email Works in 2026',
    oneLiner: 'The mental model for your first cold campaign: why list and deliverability beat copy, what healthy numbers look like in AI-saturated inboxes, and why small beats volume.',
    markdown: howColdEmailWorks,
  },
  {
    slug: 'building-your-list',
    title: 'Building Your List',
    oneLiner: 'Define one persona, write a one-sentence ICP, and build a verified list that will not burn your sending domain.',
    markdown: buildingYourList,
  },
  {
    slug: 'signals-and-timing',
    title: 'Signals and Timing',
    oneLiner: 'How to find the slice of your market that is ready now: buying signals, tiered effort, trigger-based sequences, and when to come back to everyone else.',
    markdown: signalsAndTiming,
  },
  {
    slug: 'deliverability',
    title: 'Deliverability',
    oneLiner: 'Set up authentication, a secondary sending domain, warmed and capped mailboxes, and honest monitoring so your cold email actually reaches the inbox.',
    markdown: deliverability,
  },
  {
    slug: 'writing-emails-that-get-replies',
    title: 'Writing Emails That Get Replies',
    oneLiner: 'The copy fundamentals for a first cold email in 2026: under 80 words, plain text, one interest ask, and nothing that reads as automation.',
    markdown: writingEmails,
  },
  {
    slug: 'follow-ups-and-sequences',
    title: 'Follow-ups and Sequences',
    oneLiner: 'How to plan a three to five touch cold outreach sequence that adds something new each time, stops on any reply, and protects your domain.',
    markdown: followUps,
  },
  {
    slug: 'beyond-email',
    title: 'Beyond Email',
    oneLiner: 'What works on the phone, on LinkedIn, and in the post in 2026, and how to layer channels without multiplying your risk.',
    markdown: beyondEmail,
  },
  {
    slug: 'measuring-results',
    title: 'Measuring Results',
    oneLiner: 'How to read cold outreach numbers without fooling yourself: replies over opens, sample sizes that mean something, and the right order to diagnose a campaign that underperforms.',
    markdown: measuringResults,
  },
  {
    slug: 'the-economics-of-cold-outreach',
    title: 'The Economics of Cold Outreach',
    oneLiner: 'The capacity ceilings, funnel arithmetic, and market-size gate that decide whether cold email can work for your business at all.',
    markdown: economics,
  },
  {
    slug: 'tactics-that-died',
    title: 'Tactics That Died',
    oneLiner: 'The reference list of outreach tactics that flipped from advantage to automation tell, why each one died, and what to do instead.',
    markdown: tacticsThatDied,
  },
];

export function guideBySlug(slug: string): GuideMeta | undefined {
  return guides.find(g => g.slug === slug);
}
